import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import api from '../services/api.js';
import toast from 'react-hot-toast';

const roleLabel = (role, isSpare) => {
  if (role === 'goalie')  return isSpare ? 'Spare Goalie' : 'Goalie';
  // Apr 30 2026 (App_Change_List #1) — two-role model: legacy "Team
  // Manager" invites surface as "Coach" in the UI so the user-facing
  // app shows only Coach + Player. Backend still remembers the
  // original role on the invite row for back-compat.
  if (role === 'manager') return 'Coach';
  if (role === 'coach')   return 'Coach';
  return isSpare ? 'Spare Player' : 'Player';
};

export default function InviteAcceptPage() {
  const { token }   = useParams();
  const navigate    = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const acceptedRef = useRef(false);
  // Tracks whether we've finished the "log out any pre-existing
  // session before loading" step. We do this every time the user
  // lands on /invite/:token because sharing invite links between
  // users on the same device (or between tabs of a single user)
  // kept triggering stale-session errors on accept. The cleanest
  // answer is: invite links always start from a clean, logged-out
  // slate. If the user needs to be logged in to accept, they'll
  // log in again after seeing the preview — no data is lost, only
  // the session handle.
  const [sessionCleared, setSessionCleared] = useState(false);

  const [status,  setStatus]  = useState('loading');
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState('');
  // v2.5.176 — when the preview fails for a non-invite reason (network /
  // browser / 5xx), show a Retry button instead of a dead end.
  const [canRetry, setCanRetry] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  // Whether the logged-in user has explicitly confirmed the pre-
  // assignment the coach baked into the invite. For shareable-link
  // invites (or any invite that binds to ≥1 seasons), we show a
  // confirmation step instead of silently accepting — the joiner gets
  // a chance to see "you're about to become a Spare Player in Fall
  // 2025" and either confirm or cancel.
  const [confirmedPreAssignment, setConfirmedPreAssignment] = useState(false);

  // ── Step 0 — force-logout any existing session BEFORE preview load ──
  // When a user clicks an invite link while already logged in (often
  // in another tab, or as a different user who shared a device), the
  // existing JWT was being sent with the preview call and then later
  // with the /invite/:token accept call. That caused errors like
  // "already a member of this team" or 403s when the server's view
  // of the current user didn't match the invite's intended recipient.
  //
  // Per product decision: invite links always start fresh. If there's
  // ANY token in localStorage when we land here, wipe it synchronously,
  // preserve the invite token under pendingInviteToken so the login
  // flow can auto-accept afterwards, then call logout() to clear
  // auth state + revoke server-side refresh token. Only after this
  // runs do we load the preview (so the preview fetch itself goes
  // out without stale auth).
  //
  // Gated on !authLoading: AuthContext's initial /auth/me call can
  // race with our logout — if we clear localStorage while /auth/me is
  // in flight, the response can set user back to populated AFTER our
  // setUser(null). Waiting for authLoading=false means /auth/me has
  // either resolved (user populated → we logout) or failed (no user →
  // nothing to clear).
  useEffect(() => {
    if (authLoading) return;
    if (sessionCleared) return;
    const clearAndMark = async () => {
      const existingToken = localStorage.getItem('token');
      if (existingToken) {
        // Save the invite token so the downstream login flow picks
        // it up (AuthContext.login reads pendingInviteToken and
        // auto-accepts). We save BEFORE calling logout() because
        // logout() itself wipes pendingInviteToken as part of its
        // "forget everything" contract — we don't want to modify
        // logout() since other callers depend on that behaviour.
        localStorage.setItem('pendingInviteToken', token);
        try {
          await logout();
        } catch {
          // logout is best-effort; worst case the server-side refresh
          // token stays alive for a bit. Local state is cleared by
          // logout() regardless of the server call outcome.
        }
        // Re-save in case logout()'s removeItem('pendingInviteToken')
        // raced us. Idempotent — writes the same token.
        localStorage.setItem('pendingInviteToken', token);
      }
      setSessionCleared(true);
    };
    clearAndMark();
    // Only runs on mount / token change — logout is stable from context.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authLoading]);

  // Step 1 — load preview (public, no auth).
  // We pass an Authorization header when possible so the preview
  // endpoint can tell us if the logged-in user is ALREADY on the
  // team (for the "you're already here" message) without requiring
  // the client to make a second authenticated call.
  //
  // Gated on sessionCleared so the preview call doesn't carry a stale
  // JWT that was about to be logged out.
  useEffect(() => {
    if (!sessionCleared) return;
    setCanRetry(false);
    const load = async () => {
      try {
        // v2.5.176 — call the PUBLIC preview with skipAuth so a stale
        // token from a prior session is never attached. Previously the
        // request interceptor would attach any localStorage token; if it
        // was expired/invalid the response interceptor could try a
        // refresh and, on failure, wipe storage and redirect to /login —
        // turning a fine public invite link into a dead "Could not load"
        // state. The "already on this team" detection happens after login
        // anyway (handleInviteToken returns success for existing members),
        // so the preview doesn't need the header.
        const { data } = await api.get(`/invite-preview/${token}`, { skipAuth: true });
        setPreview(data);
      } catch (err) {
        const reason   = err.response?.data?.error;
        const teamName = err.response?.data?.teamName;
        // v2.5.176 — distinguish a genuine invite problem (the server
        // responded with a reason code) from a request that never got a
        // usable response at all. The latter (err.response undefined, or
        // a 5xx / non-JSON body) is NOT an invite problem — it's a
        // network/browser/transient issue. Paul hit this in Firefox (but
        // not Edge), landing on the generic "Could not load invite
        // details" with no way forward. We now detect the no-response
        // case and tell the truth + offer a retry, instead of implying
        // the invite itself is bad.
        if (reason === 'already_accepted') {
          setMessage(teamName
            ? `This invite was already used — you're on ${teamName}. Just log in to access your team.`
            : "This invite was already used — you're already on the team. Just log in to access it.");
        } else if (reason === 'expired') {
          setMessage(teamName
            ? `This invite to ${teamName} has expired. Ask your coach to send a new one.`
            : 'This invite link has expired. Ask your coach to send a new one.');
        } else if (reason === 'invalid') {
          setMessage('This invite link is invalid — it may have been mistyped or truncated. Ask your coach to resend it.');
        } else if (!err.response || err.response.status >= 500) {
          // No HTTP response (network blocked / offline / CORS / a
          // browser privacy setting), or a server error. The invite link
          // is probably fine — this is a loading problem.
          setMessage("We couldn't reach Game Day to load this invite — this is usually a network or browser issue, not a problem with the invite itself. Try again, or open the link in a different browser.");
          setCanRetry(true);
        } else {
          // A 4xx we don't have a specific code for — still likely the
          // link, so guide them to retry/resend.
          setMessage('Could not load invite details. Try again, or ask your coach to resend the invite.');
          setCanRetry(true);
        }
        setStatus('error');
        return;
      }
      if (user) {
        // Don't auto-accept yet — let the user review the pre-
        // assignment first. The "joining" path kicks in only after
        // they click "Confirm & Join" below.
        setStatus('confirm');
      } else {
        localStorage.setItem('pendingInviteToken', token);
        setStatus('preview');
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, sessionCleared, retryNonce]);

  // Step 2 — if user signs in mid-session and the preview is already
  // loaded, surface the confirmation step.
  useEffect(() => {
    if (user && status === 'preview' && !acceptedRef.current) {
      setStatus('confirm');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, status]);

  const acceptInvite = async () => {
    if (acceptedRef.current) return;
    acceptedRef.current = true;
    setStatus('joining');
    try {
      await api.get(`/invite/${token}`);
      localStorage.removeItem('pendingInviteToken');
      toast.success(`Welcome to ${preview?.teamName || 'the team'}! 🏒`);
      setStatus('done');
      setTimeout(() => navigate('/dashboard'), 1800);
    } catch (err) {
      // Reset the ref so a retry doesn't get locked out. The user can
      // tap Confirm again from the confirm view (if they land back
      // there) or use the Try Again button on the error view.
      acceptedRef.current = false;
      const reason = err.response?.data?.error || '';
      setMessage(reason === 'expired'
        ? 'This invite link has expired.'
        : 'Could not accept invite — please try again.');
      setStatus('error');
    }
  };

  const Shell = ({ children }) => (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', padding: '24px 16px',
    }}>
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none',
        backgroundImage: `linear-gradient(rgba(0,212,255,0.04) 1px, transparent 1px),
                          linear-gradient(90deg, rgba(0,212,255,0.04) 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
      }} />

      {/* Game Day wordmark */}
      <div style={{ marginBottom: 28, textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <p style={{
          fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 22,
          letterSpacing: '0.12em', textTransform: 'uppercase',
          color: 'var(--ice)', margin: 0,
        }}>GAME DAY</p>
        <p style={{ fontSize: 11, color: 'var(--text-2)', letterSpacing: '0.08em', margin: '2px 0 0', textTransform: 'uppercase' }}>
          Team Manager
        </p>
      </div>

      <div className="glass animate-slide-up" style={{
        width: '100%', maxWidth: 400, padding: '32px 28px',
        textAlign: 'center', position: 'relative', zIndex: 1,
        borderRadius: 20,
      }}>
        {children}
      </div>

      <p style={{ marginTop: 20, fontSize: 11, color: 'var(--text-2)', position: 'relative', zIndex: 1 }}>
        Powered by <strong style={{ color: 'var(--ice)' }}>Game Day</strong> · app.game-day.app
      </p>
    </div>
  );

  if (status === 'loading') return (
    <Shell>
      <div className="spinner mx-auto" style={{ width: 28, height: 28 }} />
      <p style={{ marginTop: 14, fontSize: 14, color: 'var(--text-2)' }}>Loading your invite…</p>
    </Shell>
  );

  if (status === 'error') return (
    <Shell>
      <div style={{ fontSize: 48, marginBottom: 12 }}>❌</div>
      <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, margin: '0 0 8px', color: 'var(--text)' }}>
        Invite Unavailable
      </h2>
      <p style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 24, lineHeight: 1.6 }}>{message}</p>
      {/* If the user is logged in and the invite is still loadable
          (preview resolved), give them a Try Again button that re-
          enters the confirm flow. Useful when the failure was
          transient (network blip, DB restart, etc.) rather than a
          terminal "expired" case. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
        {canRetry && (
          <button
            onClick={() => {
              // Re-run the preview load. Reset to loading + bump the
              // nonce so the load effect fires again. (v2.5.176)
              setMessage('');
              setCanRetry(false);
              setStatus('loading');
              setRetryNonce(n => n + 1);
            }}
            className="btn-ice"
            style={{ padding: '10px 20px', fontSize: 13 }}>
            Try Again
          </button>
        )}
        {user && preview && (
          <button
            onClick={() => {
              setConfirmedPreAssignment(false);
              setStatus('confirm');
              setMessage('');
            }}
            className="btn-ice"
            style={{ padding: '10px 20px', fontSize: 13 }}>
            Try Again
          </button>
        )}
        <Link to="/login" className="btn-ghost" style={{ display: 'inline-flex' }}>
          Sign In to Game Day
        </Link>
      </div>
    </Shell>
  );

  if (status === 'joining') return (
    <Shell>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🏒</div>
      <div className="spinner mx-auto" style={{ width: 24, height: 24 }} />
      <p style={{ marginTop: 14, fontSize: 14, color: 'var(--text-2)' }}>
        Adding you to {preview?.teamName ? `"${preview.teamName}"` : 'the team'}…
      </p>
    </Shell>
  );

  if (status === 'done') return (
    <Shell>
      <div style={{ fontSize: 52, marginBottom: 8 }}>🎉</div>
      <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22, color: 'var(--goal)', margin: '0 0 8px' }}>
        You're On The Team!
      </h2>
      {preview?.teamName && (
        <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
          {preview.teamName}
        </p>
      )}
      <p style={{ fontSize: 13, color: 'var(--text-2)' }}>Redirecting to your dashboard…</p>
    </Shell>
  );

  // ── Confirm step (logged-in, before acceptance) ──
  // Only shown when the user is signed in. Gives them a chance to see:
  //   - which team they're joining (always)
  //   - their pre-assigned role/classification (always)
  //   - which seasons they'll be added to (when invite has season_ids)
  //   - a "you're already on this team" heads-up (when alreadyOnTeam is set)
  // On confirm → acceptInvite(). On cancel → navigate home.
  if (status === 'confirm') {
    const { alreadyOnTeam, seasons: inviteSeasons = [], isShareable } = preview || {};
    const alreadyHere = !!alreadyOnTeam;
    const newSeasons  = alreadyHere
      ? inviteSeasons.filter(s => !alreadyOnTeam.alreadyInSeasonIds.includes(s.id))
      : inviteSeasons;
    const existingSeasons = alreadyHere
      ? inviteSeasons.filter(s => alreadyOnTeam.alreadyInSeasonIds.includes(s.id))
      : [];
    const nothingToDo = alreadyHere && newSeasons.length === 0;
    return (
      <Shell>
        <div style={{ fontSize: 46, marginBottom: 10 }}>{alreadyHere ? '👋' : '🏒'}</div>
        <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-2)', margin: '0 0 6px' }}>
          {alreadyHere ? "You're invited to" : "Confirm you want to join"}
        </p>
        <h1 style={{
          fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 24,
          letterSpacing: '0.04em', textTransform: 'uppercase',
          color: 'var(--text)', margin: '0 0 12px', lineHeight: 1.15,
        }}>
          {preview?.teamName}
        </h1>

        {/* Already-on-team banner */}
        {alreadyHere && (
          <div style={{
            padding: '10px 14px', borderRadius: 10, marginBottom: 14,
            background: 'var(--ice-a3)', border: '1px solid var(--ice-a25)',
            fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, textAlign: 'left',
          }}>
            {nothingToDo ? (
              <span>
                <strong style={{ color: 'var(--ice)' }}>You're already on this team</strong>
                {existingSeasons.length > 0 && <>
                  {' '}and you're already in{' '}
                  <strong style={{ color: 'var(--text)' }}>
                    {existingSeasons.map(s => s.name).join(', ')}
                  </strong>
                </>}. Nothing to do — tap below to head to your dashboard.
              </span>
            ) : (
              <span>
                <strong style={{ color: 'var(--ice)' }}>You're already on this team.</strong>{' '}
                {newSeasons.length > 0 && <>Accepting will add you to{' '}
                  <strong style={{ color: 'var(--text)' }}>
                    {newSeasons.map(s => s.name).join(', ')}
                  </strong>.</>}
              </span>
            )}
          </div>
        )}

        {/* Role chip — use the roleLabel helper */}
        {!alreadyHere && (
          <span style={{
            display: 'inline-block', marginBottom: 14,
            fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
            padding: '4px 12px', borderRadius: 99,
            background: 'var(--ice-a12)', color: 'var(--ice)',
            border: '1px solid var(--ice-a25)',
          }}>
            You'll join as: {roleLabel(preview?.role, preview?.isSpare)}
          </span>
        )}

        {/* Season list — only when invite binds to ≥1 seasons */}
        {inviteSeasons.length > 0 && !nothingToDo && (
          <div style={{ textAlign: 'left', marginBottom: 16 }}>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-2)', margin: '0 0 6px' }}>
              {alreadyHere ? "New seasons you'll join:" : "You'll be added to:"}
            </p>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {(alreadyHere ? newSeasons : inviteSeasons).map(s => (
                <li key={s.id} style={{
                  fontSize: 13, color: 'var(--text)',
                  padding: '6px 10px', marginBottom: 4,
                  background: 'var(--surface-2)', borderRadius: 8,
                  border: '1px solid var(--border)',
                }}>
                  🏆 {s.name}
                </li>
              ))}
            </ul>
          </div>
        )}

        {isShareable && !alreadyHere && (
          <p style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 12, lineHeight: 1.5 }}>
            You're joining via a shareable team link — your coach will see you in the roster right away.
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          <button
            onClick={() => { setConfirmedPreAssignment(true); acceptInvite(); }}
            disabled={confirmedPreAssignment}
            className="btn-ice w-full justify-center"
            style={{ padding: '14px 20px', fontSize: 14, fontWeight: 800, letterSpacing: '0.07em' }}>
            {nothingToDo ? 'Go to Dashboard' : 'Confirm & Join'}
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-ghost w-full justify-center"
            style={{ padding: '12px 20px', fontSize: 13 }}>
            Cancel
          </button>
        </div>
      </Shell>
    );
  }

  // preview state — not logged in
  return (
    <Shell>
      <div style={{ fontSize: 52, marginBottom: 16, lineHeight: 1 }}>🏒</div>

      <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-2)', margin: '0 0 6px' }}>
        You've been invited to join
      </p>

      <h1 style={{
        fontFamily: 'var(--font-display)', fontWeight: 900,
        fontSize: preview?.teamName ? 28 : 22,
        letterSpacing: '0.04em', textTransform: 'uppercase',
        color: 'var(--text)', margin: '0 0 8px', lineHeight: 1.15,
      }}>
        {preview?.teamName || 'Your Team'}
      </h1>

      {preview && (
        <span style={{
          display: 'inline-block', marginBottom: 10,
          fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
          padding: '3px 10px', borderRadius: 99,
          background: 'var(--ice-a12)', color: 'var(--ice)',
          border: '1px solid var(--ice-a25)',
        }}>
          {roleLabel(preview.role, preview.isSpare)}
        </span>
      )}

      {preview?.coachName && (
        <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 0 }}>
          Invited by <strong style={{ color: 'var(--text)' }}>{preview.coachName}</strong>
        </p>
      )}

      {/* ── Season list (Apr 2026) ──
          If the invite binds to one or more seasons (schedule-specific
          invite from the coach), surface them here so the joiner knows
          EXACTLY which schedule they're being added to. This avoids
          the "wait, am I creating a team or joining a team?" confusion
          that prompted the bug report — the coach's intent ("join my
          Thursday Skate schedule") needs to be visible upfront. */}
      {preview?.seasons && preview.seasons.length > 0 && (
        <div style={{ textAlign: 'left', marginTop: 16 }}>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-2)', margin: '0 0 6px' }}>
            You'll be added to {preview.seasons.length === 1 ? 'this schedule:' : 'these schedules:'}
          </p>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {preview.seasons.map(s => (
              <li key={s.id} style={{
                fontSize: 13, color: 'var(--text)',
                padding: '6px 10px', marginBottom: 4,
                background: 'var(--surface-2)', borderRadius: 8,
                border: '1px solid var(--border)',
              }}>
                🏆 {s.name}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ height: 1, background: 'var(--border)', margin: '20px 0' }} />

      <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.65, marginBottom: 22 }}>
        <strong style={{ color: 'var(--text)' }}>Game Day</strong> is the free app your team
        uses to manage schedules, RSVP for games, and stay connected.
        Takes 30 seconds to set up.
      </p>

      {/* ── CTAs (Apr 2026) ──
          Primary action: accept the invite. Since the coach specified a
          team (and optionally a schedule), the joiner's path is always
          "accept this specific invite", never "create a new team".
          We express both CTAs as "Accept Invite" / "Sign In & Accept"
          so the language matches the coach's intent — a reported bug
          was users landing on Dashboard thinking they needed to
          "Create Your Team" when really they were just supposed to
          join the team the coach had invited them to. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Link to={`/register?invite=${token}`} className="btn-ice w-full justify-center"
          style={{ padding: '14px 20px', fontSize: 14, fontWeight: 800, letterSpacing: '0.07em' }}>
          Accept Invite &amp; Create Account
        </Link>
        <Link to={`/login?invite=${token}`} className="btn-ghost w-full justify-center"
          style={{ padding: '12px 20px', fontSize: 13, borderColor: 'var(--border)' }}>
          I already have an account — Sign In &amp; Accept
        </Link>
      </div>

      <p style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 16, opacity: 0.7 }}>
        Free · No credit card · Your data is encrypted
      </p>
    </Shell>
  );
}
